//region header */
//--------------------------------------------------------------------------------------------------
//
//  Description: Gazetteer Page
//
//  Copyright:    © 2021 - 2026 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//region Version 1.0.0.0
//    001   20.07.21 Sean Flook                  Initial Revision.
//    002   18.04.24 Sean Flook        IMANN-351 Changes required to reload the contexts after a refresh.
//endregion Version 1.0.0.0
//region Version 1.0.5.0
//    003   27.01.25 Sean Flook       IMANN-1077 Upgraded MUI to v6.
//    004   23.04.25 Joshua McCormick IMANN-1743 Commented out all sessionStorage calls
//    005   24.04.25 Sean Flook       IMANN-1760 Reenabled above change as required.
//endregion Version 1.0.5.0
//region Version 1.0.7.0
//    006   13.06.25 Sean Flook       IMANN-1812 Set the initial extent for the map to the authority extent.
//    007   28.08.25 Sean Flook       IMANN-1812 Changes required to update MUI to v7.
//    008   24.10.25 Sean Flook       IMANN-1129 Changed to only display the map if there are no search results.
//    009   27.10.25 Sean Flook       IMANN-2092 Use the new spatialReferenceWkid property from mapContext to set the spatialReference used by the layers in the map.
//    010   27.10.25 Sean Flook       IMANN-2111 Added react-resizable-panels.
//    011   28.10.25 Sean Flook       IMANN-2111 Change colour of resize handles when hovering cursor over them.
//    012   06.11.25 Sean Flook       IMANN-2092 Correctly set the spatialReferenceWkid variable.
//    013   26.02.26 Sean Flook       IMANN-2324 Changed order when reloading contexts.
//    014   07.04.26 Sean Flook       IMANN-2395 Added timeouts to ensure data is loaded correctly and set mapContext.onViewAuthorityExtent if reloading with no search data.
//endregion Version 1.0.7.0
//
//--------------------------------------------------------------------------------------------------
//endregion header */

import React, { useContext, useState, useRef, useEffect } from "react";

import UserContext from "../context/userContext";
import MapContext from "../context/mapContext";
import LookupContext from "../context/lookupContext";
import SearchContext from "./../context/searchContext";
import SettingsContext from "../context/settingsContext";
import FilterContext from "../context/filterContext";
import StreetContext from "../context/streetContext";
import PropertyContext from "../context/propertyContext";
import InformationContext from "../context/informationContext";

import { getAuthorityExtent } from "../utils/HelperUtils";

import { Box } from "@mui/material";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";

import SearchDataForm from "../forms/SearchDataForm";
import ADSMap from "../components/ADSMap";

import { dataFormStyle } from "../utils/ADSStyles";
import { adsLightGreyD } from "../utils/ADSColours";

function GazetteerPage() {
  const userContext = useContext(UserContext);
  const mapContext = useContext(MapContext);
  const lookupContext = useContext(LookupContext);
  const searchContext = useContext(SearchContext);
  const settingsContext = useContext(SettingsContext);
  const filterContext = useContext(FilterContext);
  const streetContext = useContext(StreetContext);
  const propertyContext = useContext(PropertyContext);
  const informationContext = useContext(InformationContext);

  const [reloadContexts, setReloadContexts] = useState(false);

  const getStartExtent = () => {
    if (mapContext.currentExtent) {
      return {
        xmin: mapContext.currentExtent.xmin,
        ymin: mapContext.currentExtent.ymin,
        xmax: mapContext.currentExtent.xmax,
        ymax: mapContext.currentExtent.ymax,
        zoom: mapContext.currentExtent.zoom,
        spatialReference: mapContext.currentExtent.spatialReference,
      };
    } else if (settingsContext && settingsContext.authorityCode > 0) {
      return getAuthorityExtent(
        settingsContext.authorityCode,
        settingsContext.isScottish,
        settingsContext.isWelsh,
        mapContext.spatialReference || 27700
      );
    }
    return null;
  };

  const startExtent = useRef(getStartExtent());

  useEffect(() => {
    if (sessionStorage.getItem("GazetteerPage_firstLoadDone") === null) {
      sessionStorage.setItem("GazetteerPage_firstLoadDone", 1);
    } else {
      setReloadContexts(true);
    }
  }, []);

  useEffect(() => {
    if (reloadContexts) {
      setReloadContexts(false);

      // Reload user context first
      userContext.onReload();

      // Then reload settings context (depends on user context)
      setTimeout(() => {
        settingsContext.onReload();

        // Then reload search contexts (depends on user and settings contexts)
        setTimeout(() => {
          searchContext.onReload();

          // Finally reload all other contexts (depend on user, settings and search contexts)
          setTimeout(() => {
            lookupContext.onReload();
            filterContext.onReload();
            streetContext.onReload();
            propertyContext.onReload();
            informationContext.onReload();
            mapContext.onReload();
          }, 0);
        }, 0);
      }, 0);
    }
  }, [
    reloadContexts,
    userContext,
    lookupContext,
    searchContext,
    settingsContext,
    mapContext,
    filterContext,
    streetContext,
    propertyContext,
    informationContext,
  ]);

  useEffect(() => {
    if (
      !startExtent.current &&
      searchContext.currentSearchData?.searchString === "!+reload+!" &&
      settingsContext?.authorityCode > 0
    ) {
      mapContext.onViewAuthorityExtent(true);
    }
  }, [mapContext, searchContext.currentSearchData, settingsContext]);

  return (
    <Box sx={dataFormStyle("ResizePanels")}>
      <PanelGroup autoSaveId="gazetteerPagePanels" direction="horizontal">
        {searchContext.currentSearchData?.results?.length > 0 && (
          <>
            <Panel id="searchDataFormPanel" defaultSize={35} minSize={30} order={1}>
              <SearchDataForm />
            </Panel>
            <PanelResizeHandle>
              <Box
                sx={{
                  width: "5px",
                  cursor: "col-resize",
                  height: "100%",
                  backgroundColor: "transparent",
                  "&:hover": {
                    backgroundColor: adsLightGreyD,
                  },
                }}
              />
            </PanelResizeHandle>
          </>
        )}
        <Panel id="gazetteerMapPanel" defaultSize={65} minSize={30} order={2}>
          <ADSMap mapType="gazetteer" startExtent={startExtent.current} />
        </Panel>
      </PanelGroup>
    </Box>
  );
}

export default GazetteerPage;
